/**
 * Push Notification Utilities for Frontend
 *
 * Handles subscribing to push notifications, requesting permissions,
 * and managing push subscriptions.
 */

import { getSessionToken } from '@/lib/better-auth';

// Import API client functions
async function apiPost<T>(endpoint: string, data: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const sessionToken = getSessionToken();
  if (sessionToken && sessionToken !== 'undefined') {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  const API_BASE_URL = import.meta.env.DEV ? import.meta.env.VITE_API_URL : '/api';
  const url = `${API_BASE_URL}${endpoint}`;

  // Better-auth handles sessions via cookies, no bearer token needed
  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include', // Include cookies for better-auth session
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // console.error('[Push] API Error:', errorData)
    throw new Error(errorData.message || errorData.error || response.statusText);
  }

  const result = await response.json();
  return result;
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/**
 * Convert a PushSubscription to a serializable format
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Request permission for push notifications
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  // console.log('[Push] Requesting notification permission...')

  if (!('Notification' in window)) {
    console.error('[Push] Notifications not supported in this browser');
    throw new Error('This browser does not support notifications');
  }

  // console.log('[Push] Current notification permission:', Notification.permission)

  if (Notification.permission === 'granted') {
    console.log('[Push] ✓ Notification permission already granted');
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('[Push] ✗ Notification permission denied');
    throw new Error(
      'Notification permission has been denied. Please enable it in your browser settings.',
    );
  }

  // Request permission
  // console.log('[Push] Requesting permission from user...')
  const permission = await Notification.requestPermission();
  // console.log('[Push] User responded with permission:', permission)
  return permission;
}

/**
 * Get or create push subscription
 */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  // console.log('[Push] Getting push subscription...')

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // console.warn('[Push] ✗ Push messaging is not supported')
    return null;
  }

  try {
    // console.log('[Push] Waiting for service worker to be ready...')
    const registration = await navigator.serviceWorker.ready;
    // console.log('[Push] Service worker ready:', registration.scope)

    // Check for existing subscription
    // console.log('[Push] Checking for existing subscription...')
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // console.log('[Push] ✓ Found existing subscription')
      // console.log('[Push] Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...')
      return subscription;
    }

    // console.log('[Push] No existing subscription found')
    // If no subscription exists, we'll need to create one
    // But we need VAPID public key from the server first
    // For now, return null and let the caller handle it
    return null;
  } catch (error) {
    // console.error('[Push] ✗ Error getting push subscription:', error)
    if (error instanceof Error) {
      console.error('[Push] Error message:', error.message);
      console.error('[Push] Error stack:', error.stack);
    }
    return null;
  }
}

/**
 * Subscribe to push notifications
 *
 * Note: You'll need to get the VAPID public key from your server
 * and pass it here. For now, this is a placeholder implementation.
 */
export async function subscribeToPushNotifications(
  vapidPublicKey: string,
): Promise<PushSubscription> {
  // console.log('[Push] Starting push subscription...')
  // console.log('[Push] VAPID public key present:', !!vapidPublicKey)
  // console.log('[Push] VAPID public key length:', vapidPublicKey?.length || 0)

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // console.error('[Push] ✗ Push messaging is not supported')
    throw new Error('Push messaging is not supported');
  }

  try {
    // console.log('[Push] Waiting for service worker to be ready...')
    const registration = await navigator.serviceWorker.ready;
    // console.log('[Push] Service worker ready:', registration.scope)

    // Check if already subscribed
    // console.log('[Push] Checking for existing subscription...')
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // console.log('[Push] ✓ Already subscribed')
      // console.log('[Push] Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...')
      return subscription;
    }

    // console.log('[Push] No existing subscription, creating new one...')

    // Convert VAPID public key to Uint8Array
    // console.log('[Push] Converting VAPID public key to Uint8Array...')
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    // console.log('[Push] Application server key converted, length:', applicationServerKey.length)
    // console.log('[Push] First byte:', '0x' + applicationServerKey[0].toString(16))

    // Validate key format
    if (applicationServerKey.length !== 65) {
      throw new Error(
        `Invalid VAPID key length: expected 65 bytes, got ${applicationServerKey.length}`,
      );
    }
    if (applicationServerKey[0] !== 0x04) {
      throw new Error(
        `Invalid VAPID key format: expected 0x04 prefix, got 0x${applicationServerKey[0].toString(16)}`,
      );
    }

    // Detect Android for better error messages
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    console.log('[Push] Platform detection:', {
      isAndroid,
      isChrome,
      userAgent: navigator.userAgent,
    });

    // Subscribe to push notifications
    // console.log('[Push] Calling pushManager.subscribe()...')
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as any,
    });

    // console.log('[Push] ✓ Push subscription created successfully!')
    // console.log('[Push] Subscription endpoint:', subscription.endpoint)
    // console.log('[Push] Subscription keys present:', {
    //     p256dh: !!subscription.getKey('p256dh'),
    //     auth: !!subscription.getKey('auth'),
    // })

    return subscription;
  } catch (error) {
    // console.error('[Push] ✗ Error subscribing to push notifications:', error)
    if (error instanceof Error) {
      // console.error('[Push] Error name:', error.name)
      // console.error('[Push] Error message:', error.message)

      // Provide helpful error messages for common issues
      if (error.name === 'DOMException') {
        if (error.message.includes('push service error')) {
          const isAndroid = /Android/i.test(navigator.userAgent);
          if (isAndroid) {
            // console.error('[Push] Android-specific push service error detected')
            // console.error('[Push] Possible causes:')
            // console.error('[Push] 1. Google Play Services not updated')
            // console.error('[Push] 2. Chrome not set as default browser')
            // console.error('[Push] 3. Background data restricted for Chrome')
            throw new Error(
              'Push notification failed: Please ensure Chrome is updated and Google Play Services is enabled. Try: Settings > Google > Security > Verify Apps',
            );
          }
        }
        if (error.message.includes('Registration failed')) {
          throw new Error(`Push subscription failed: ${error.message}`);
        }
      }
    }
    throw error;
  }
}

/**
 * Convert PushSubscription to a serializable format for storage
 */
export function serializePushSubscription(subscription: PushSubscription): PushSubscriptionData {
  // console.log('[Push] Serializing push subscription...')

  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');

  if (!key || !auth) {
    // console.error('[Push] ✗ Invalid push subscription keys')
    throw new Error('Invalid push subscription keys');
  }

  // console.log('[Push] Keys extracted, converting to base64...')

  // Convert ArrayBuffer to base64
  const keyArray = new Uint8Array(key);
  const authArray = new Uint8Array(auth);

  const p256dh = btoa(String.fromCharCode(...keyArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  const authSecret = btoa(String.fromCharCode(...authArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  // console.log('[Push] ✓ Subscription serialized')
  // console.log('[Push] Endpoint:', subscription.endpoint)
  // console.log('[Push] Keys length:', { p256dh: p256dh.length, auth: authSecret.length })

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh,
      auth: authSecret,
    },
  };
}

/**
 * Store push subscription on the server
 */
export async function storePushSubscription(
  userId: string,
  subscription: PushSubscription,
): Promise<void> {
  // console.log('[Push] Storing push subscription on server...')
  // console.log('[Push] User ID:', userId)

  const subscriptionData = serializePushSubscription(subscription);

  // console.log('[Push] Sending subscription to server...')
  // console.log('[Push] Endpoint:', subscriptionData.endpoint)

  try {
    const response = await apiPost(`/users/${userId}/push-subscription`, subscriptionData);
    // console.log('[Push] ✓ Subscription stored successfully on server')
    console.log('[Push] Server response:', response);
  } catch (error) {
    // console.error('[Push] ✗ Failed to store subscription on server:', error)
    if (error instanceof Error) {
      // console.error('[Push] Error message:', error.message)
    }
    throw error;
  }
}

/**
 * Send a test notification
 */
export async function sendTestNotification(userId: string): Promise<void> {
  // console.log('[Push] Sending test notification...')
  // console.log('[Push] User ID:', userId)

  try {
    const response = await apiPost(`/users/${userId}/test-notification`, {});
    // console.log('[Push] ✓ Test notification request sent')
    console.log('[Push] Server response:', response);
  } catch (error) {
    // console.error('[Push] ✗ Failed to send test notification:', error)
    if (error instanceof Error) {
      // console.error('[Push] Error message:', error.message)
    }
    throw error;
  }
}

/**
 * Check if push notifications are supported
 */
export function isPushNotificationSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Check if push notifications are enabled
 */
export function isPushNotificationEnabled(): boolean {
  if (!('Notification' in window)) {
    return false;
  }
  return Notification.permission === 'granted';
}
